import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { motion, useScroll, useTransform, LayoutGroup } from "framer-motion";
import { ArrowRight, Zap, ChevronDown, CheckCircle2 } from "lucide-react";
import { useRef } from "react";
import emeLogo from "@/assets/eme-logo.svg";
import { Logo } from "@/components/ui/Logo";
import { redirectToStripeCheckout } from "@/lib/stripe";
import { DecisionLayer } from "@/components/landing/DecisionLayer";
import { TrustStrip } from "@/components/landing/TrustStrip";
import { ContrastSection } from "@/components/landing/ContrastSection";
import { ExecutionTimeline } from "@/components/landing/ExecutionTimeline";
import { PremiumMetrics } from "@/components/landing/PremiumMetrics";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { GlassPanel } from "@/components/landing/GlassPanel";
import { InteractiveFeatureCards } from "@/components/marketing/InteractiveFeatureCards";
import { ConversionCTA } from "@/components/marketing/ConversionCTA";
import { ShowcaseSection } from "@/components/marketing/ShowcaseSection";
import { ThemeToggle } from "@/components/theme-toggle";
import { ScreenshotStack } from "@/components/landing/ScreenshotStack";
import { ScreenshotLightbox } from "@/components/landing/ScreenshotLightbox";
import { AngledScreenshot } from "@/components/landing/AngledScreenshot";
import { Reveal, Stagger, StaggerItem } from "@/components/landing/motion/Reveal";
import { ObliqueAppFrame } from "@/components/landing/ObliqueAppFrame";
import { TodayPreviewDemo } from "@/components/landing/demos/TodayPreviewDemo";
import { ScrollySteps } from "@/components/landing/ScrollySteps";
import { LandingScaledPreview } from "@/components/landing/LandingScaledPreview";
import { RevenueFlowPreview } from "@/components/landing/previews/RevenueFlowPreview";
import { WorkspacesProSection } from "@/components/landing/WorkspacesProSection";
import { PublicFooter } from "@/components/layout/PublicFooter";
import type { ScreenshotSpec } from "@/components/landing/types";
import { cn } from "@/lib/utils";
import { DURATION } from "@/lib/motionTokens";
import app1 from "@/assets/landing/app/APP1.png";
import app2 from "@/assets/landing/app/APP2.png";
import app3 from "@/assets/landing/app/IMMAGINI-APP.png";

export default function Landing() {
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const location = useLocation();

  // Scroll to hash (e.g. /#how-it-works) when hash is present
  useEffect(() => {
    const hash = location.hash?.replace(/^#/, "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  // Hero stack + lightbox (shared layoutId for click-to-expand)
  const stackItems = useMemo<[ScreenshotSpec, ScreenshotSpec, ScreenshotSpec]>(() => [
    { id: "today", src: app1, alt: "DSOS Today priority actions", label: "Today" },
    { id: "leads", src: app2, alt: "DSOS Leads next action list", label: "Leads" },
    { id: "revenue-flow", src: app3, alt: "DSOS Revenue Flow overview", label: "Revenue Flow" },
  ], []);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItem, setLightboxItem] = useState<ScreenshotSpec | null>(null);
  const openStackCard = (item: ScreenshotSpec) => {
    setLightboxItem(item);
    setLightboxOpen(true);
  };

  // Section images (unique ids so no layoutId collision with hero stack)
  const leadsSectionShot: ScreenshotSpec = {
    id: "leads-section",
    src: app2,
    alt: "DSOS Leads mandatory next action",
    label: "Leads",
  };
  const revenueSectionShot: ScreenshotSpec = {
    id: "revenue-section",
    src: app3,
    alt: "DSOS Revenue Flow overview",
    label: "Revenue Flow",
  };

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const handleStartTrial = () => {
    redirectToStripeCheckout();
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-slate-950 dark:bg-black dark:text-white">
      {/* Navigation - Aligned with App Layout */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-card/95 backdrop-blur-sm transition-all duration-300"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <div className="container mx-auto px-4 md:px-6 h-12 md:h-12 flex items-center justify-between max-w-[var(--landing-max-width)]">
          <div className="flex items-center gap-4 md:gap-8">
            <Link to="/">
              <Logo size="sm" showText={false} />
            </Link>
            
            <nav className="hidden md:flex items-center gap-6 text-sm">
              <Link to="/product" className="text-muted-foreground hover:text-foreground transition-colors duration-[var(--duration-standard)] ease-[var(--ease-hover)]">Product</Link>
              <Link to="/#how-it-works" className="text-muted-foreground hover:text-foreground transition-colors duration-[var(--duration-standard)] ease-[var(--ease-hover)]">How It Works</Link>
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors duration-[var(--duration-standard)] ease-[var(--ease-hover)]">Pricing</Link>
              <Link to="/use-cases" className="text-muted-foreground hover:text-foreground transition-colors duration-[var(--duration-standard)] ease-[var(--ease-hover)]">Use Cases</Link>
              <Link to="/why" className="text-muted-foreground hover:text-foreground transition-colors duration-[var(--duration-standard)] ease-[var(--ease-hover)]">Why Us</Link>
            </nav>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <ThemeToggle className="h-8 w-8 shrink-0" />
            <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground h-8">
              <Link to="/auth">Login</Link>
            </Button>
            <Button 
              onClick={handleStartTrial}
              variant="default"
              size="sm"
              className="h-8"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section - Upgraded: light = normal bg + white hero box; dark = current */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center px-4 pt-16 overflow-hidden bg-background"
      >
        {/* Decision Layer background */}
        <DecisionLayer intensity="subtle" className="z-0" />

        <motion.div className="absolute inset-0 z-0" style={{ y: backgroundY }}>
          {/* Video at full opacity; readability via overlay only */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-100 dark:brightness-[0.35] brightness-[0.97] contrast-[1.05]"
          >
            <source src="/videos/hero-loop.mp4" type="video/mp4" />
          </video>
          {/* Overlay for readability — does not affect video opacity */}
          <div className="absolute inset-0 bg-background/55 dark:bg-background/75 backdrop-blur-[3px]" />
        </motion.div>

        <div className="container mx-auto max-w-[var(--landing-max-width)] text-center relative z-10 px-6">
          {/* Hero Card — single-stroke glass panel */}
          <GlassPanel className="mb-0">
            <motion.div
              className="p-10 md:p-12 relative overflow-hidden text-card-foreground"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            >
            {/* Subtle inner glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none dark:opacity-100 opacity-50" />

            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-8 relative z-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <Zap className="w-4 h-4" />
              <span>Decision & Execution Operating System</span>
            </motion.div>

            <motion.h1
              className="text-[var(--font-display)] font-semibold tracking-[var(--tracking-tighter)] mb-6 text-card-foreground leading-tight relative z-10"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Stop guessing. Start executing.
            </motion.h1>

            <motion.p
              className="text-[var(--font-body-lg)] md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-[var(--leading-body)] font-medium tracking-[var(--tracking-normal)] relative z-10"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Decision Sales OS removes bad decisions from the equation — so execution becomes automatic.
            </motion.p>

            <motion.p
              className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto mb-10 leading-[var(--leading-relaxed)] tracking-[var(--tracking-normal)] relative z-10"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
            >
              A system that reduces decisions, saves time, and protects your focus.
              <br />
              <span className="text-foreground font-semibold tracking-[var(--tracking-tight)]">Built for professionals who value efficiency over options.</span>
            </motion.p>

            {/* Hero CTAs removed - primary CTA only in nav */}

            {/* Trust Strip */}
            <div className="relative z-10">
              <TrustStrip />
            </div>

            {/* Microcopy for already paid users */}
            <motion.p
              className="mt-6 text-sm text-muted-foreground relative z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Already paid?{' '}
              <Link to="/auth" className="text-primary/80 hover:text-primary transition-colors font-medium">
                Sign in here
              </Link>
            </motion.p>
            </motion.div>
          </GlassPanel>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <motion.div 
            animate={{ y: [0, 8, 0] }} 
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="w-6 h-6 text-muted-foreground" />
          </motion.div>
        </motion.div>
      </section>

      {/* Contrast Section - Traditional CRM vs DSOS */}
      <ContrastSection />

      {/* 3 Core Principles - Interactive */}
      <section className="py-24 md:py-32 px-4 bg-gradient-to-b from-background via-secondary/20 to-background">
        <div className="container mx-auto max-w-[var(--landing-max-width)]">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)]">
                Three principles that change everything
              </h2>
              <p className="text-[var(--font-body-lg)] text-muted-foreground max-w-2xl mx-auto leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                Hover or tap a card to see how each principle transforms your daily workflow.
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <InteractiveFeatureCards />
          </ScrollReveal>
        </div>
      </section>

      {/* Daily Execution Loop - Timeline */}
      <ExecutionTimeline />

      {/* Hero stack + section images + lightbox in one LayoutGroup for shared layoutId */}
      <LayoutGroup id="hero-stack">
        {/* Today section: reveal + stagger + live preview (oblique frame + TodayPreviewDemo) */}
        <section className="pt-24 md:pt-32 pb-16 sm:pb-24 px-4 bg-gradient-to-b from-background via-secondary/20 to-background">
          <div className="container mx-auto max-w-[var(--landing-max-width)]">
            <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-10 md:gap-14">
              {/* Copy column — Reveal + Stagger */}
              <div className="md:col-span-5">
                <Reveal delay={0} y={14} blur={8}>
                  <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)] text-foreground">
                    Today: decisions, not dashboards
                  </h2>
                </Reveal>
                <Reveal delay={0.06}>
                  <p className="text-[var(--font-body-lg)] text-muted-foreground mb-6 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                    Every morning, you see exactly what must happen — mandatory actions ranked by revenue impact and time decay.
                  </p>
                </Reveal>
                <Stagger delayChildren={0.06} stagger={0.08}>
                  <ul className="space-y-3 mb-8">
                    {[
                      "Mandatory actions, ordered by DSOS priority",
                      "One-click execution: Call / WhatsApp / Send to phone",
                      "Overdue surfaced before revenue is lost",
                    ].map((bullet, index) => (
                      <StaggerItem key={index} className="flex items-start gap-3" y={10} blur={6}>
                        <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-foreground/80 leading-[var(--leading-body)]">{bullet}</span>
                      </StaggerItem>
                    ))}
                  </ul>
                </Stagger>
                <Reveal delay={0.2}>
                  <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: DURATION.micro }}>
                    <Button asChild variant="default" size="lg" className="gap-2">
                      <Link to="/product">See it in action <ArrowRight className="w-5 h-5" /></Link>
                    </Button>
                  </motion.div>
                </Reveal>
                <p className="mt-4 text-xs text-muted-foreground">Live preview — interactions disabled.</p>
              </div>
              {/* Right: viewport clamp so preview is always visible (no collapse/white) */}
              <div className="md:col-span-7 md:col-start-6 lg:justify-self-end">
                <Reveal delay={0.08}>
                  <ObliqueAppFrame badge="Today" variant="hero" className="w-full max-w-[720px]">
                    <div className="aspect-[4/3] w-full min-h-[380px] max-h-[500px] overflow-hidden bg-[#0b0b0f]">
                      <div className="h-full w-full flex items-center justify-center p-3">
                        <div className="h-full w-full max-w-full scale-[0.95] origin-center overflow-hidden rounded-lg">
                          <TodayPreviewDemo />
                        </div>
                      </div>
                    </div>
                  </ObliqueAppFrame>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* How it works: scrollytelling steps + sticky preview (live app previews) */}
        <ScrollySteps id="how-it-works" />

        {/* Leads section: oblique frame + Lead detail preview (different from Today above) */}
        <section className="pt-12 pb-20 px-4 bg-background overflow-visible">
          <div className="container mx-auto max-w-[var(--landing-max-width)] overflow-visible">
            <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-10 md:gap-14 overflow-visible">
              {/* Oblique preview left (reverse order on md) — clickable, scroll to CTA */}
              <motion.div
                className="relative flex justify-center md:justify-end md:col-span-7 order-2 md:order-1 self-center overflow-visible cursor-pointer"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15% 0px -15% 0px" }}
                transition={{ duration: 0.2, ease: [0, 0, 0.2, 1] }}
                role="button"
                tabIndex={0}
                aria-label="View live preview — scroll to CTA"
                onClick={() => document.getElementById("landing-cta")?.scrollIntoView({ behavior: "smooth" })}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); document.getElementById("landing-cta")?.scrollIntoView({ behavior: "smooth" }); } }}
              >
                <div className="w-full max-w-[560px] [transform:rotate(-2deg)] pointer-events-auto">
                  <ObliqueAppFrame badge="Today" className="w-full">
                    <div className="aspect-[4/3] w-full min-h-[380px] max-h-[500px] overflow-hidden bg-[#0b0b0f]">
                      <LandingScaledPreview baseW={980} baseH={620}>
                        <TodayPreviewDemo />
                      </LandingScaledPreview>
                    </div>
                  </ObliqueAppFrame>
                </div>
                <p className="mt-2 text-center text-xs text-muted-foreground">Today — mandatory next actions.</p>
              </motion.div>
              {/* Copy right */}
              <motion.div
                className="md:col-span-5 md:col-start-8 order-1 md:order-2"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)] text-foreground">
                  Leads: mandatory next action
                </h2>
                <p className="text-[var(--font-body-lg)] text-muted-foreground mb-6 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                  Each lead shows the next move and why it matters — no interpretation required.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Auto-scored leads with temperature decay",
                    "Clear next step: call, WhatsApp, follow-up",
                    "Contextual 'why now' on every action",
                  ].map((bullet, index) => (
                    <motion.li
                      key={index}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.4 }}
                    >
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80 leading-[var(--leading-body)]">{bullet}</span>
                    </motion.li>
                  ))}
                </ul>
                <Button asChild variant="default" size="lg" className="gap-2">
                  <Link to="/product">Explore features <ArrowRight className="w-5 h-5" /></Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Revenue Flow section: centered hero render, pill overlay, spotlight, no caption */}
        <section className="py-24 md:py-32 px-4 bg-gradient-to-b from-background via-secondary/20 to-background overflow-visible">
          <div className="container mx-auto max-w-[var(--landing-max-width)] overflow-visible">
            <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-10 md:gap-14 overflow-visible">
              {/* Copy left */}
              <motion.div
                className="md:col-span-5 order-2 md:order-1"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)] text-foreground">
                  Revenue Flow: revenue risk made visible
                </h2>
                <p className="text-[var(--font-body-lg)] text-muted-foreground mb-6 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                  See how much revenue is at risk right now. Every missed action has a price tag.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Revenue at Risk surfaced daily",
                    "Missed follow-ups escalate automatically",
                    "Inaction becomes visible before it becomes loss",
                  ].map((bullet, index) => (
                    <motion.li
                      key={index}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.4 }}
                    >
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80 leading-[var(--leading-body)]">{bullet}</span>
                    </motion.li>
                  ))}
                </ul>
                <Button variant="default" size="lg" className="gap-2" onClick={handleStartTrial}>
                  Start free trial <ArrowRight className="w-5 h-5" />
                </Button>
              </motion.div>
              {/* Render right — centered, spotlight, clickable, scroll to CTA */}
              <motion.div
                className="relative flex items-center justify-center md:col-span-7 md:col-start-6 order-1 md:order-2 self-center overflow-visible py-6 md:py-8 cursor-pointer"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15% 0px -15% 0px" }}
                transition={{ duration: 0.2, ease: [0, 0, 0.2, 1] }}
                role="button"
                tabIndex={0}
                aria-label="View Revenue Flow — scroll to CTA"
                onClick={() => document.getElementById("landing-cta")?.scrollIntoView({ behavior: "smooth" })}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); document.getElementById("landing-cta")?.scrollIntoView({ behavior: "smooth" }); } }}
              >
                {/* Spotlight behind render */}
                <div
                  className="pointer-events-none absolute inset-0 flex items-center justify-center -z-10"
                  aria-hidden
                >
                  <div
                    className="w-[min(100%,600px)] h-[75%] max-h-[420px] opacity-40"
                    style={{
                      background: "radial-gradient(ellipse 70% 70% at 50% 50%, rgba(59,130,246,0.12) 0%, transparent 70%)",
                    }}
                  />
                </div>
                <div className="relative w-full max-w-[600px] flex items-center justify-center pointer-events-auto">
                  <div
                    className={cn(
                      "relative w-full max-w-[600px] aspect-[16/11] min-h-[280px] md:min-h-[340px]",
                      "transform transition-transform duration-200",
                      "rotate-[2deg] md:rotate-[-4deg]"
                    )}
                  >
                    <ObliqueAppFrame badge="Revenue Flow" className="w-full h-full">
                      <div className="aspect-[4/3] w-full h-full min-h-[280px] overflow-hidden bg-[#0b0b0f] rounded-b-[20px] flex items-center justify-center p-2">
                        <LandingScaledPreview baseW={920} baseH={560} className="h-full w-full max-w-full max-h-full">
                          <RevenueFlowPreview />
                        </LandingScaledPreview>
                      </div>
                    </ObliqueAppFrame>
                    {/* Pill overlay top-right on render */}
                    <span
                      className={cn(
                        "absolute top-3 right-3 z-10 inline-flex rounded-full px-3 py-1.5 text-xs font-medium backdrop-blur-sm",
                        "bg-white/5 dark:bg-white/10 border border-white/10 text-foreground/90"
                      )}
                    >
                      Revenue Flow • In play • Won • Lost
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 3 different workspaces (Pro) — text left, oblique render right */}
        <WorkspacesProSection />

        <ScreenshotLightbox open={lightboxOpen} onOpenChange={setLightboxOpen} item={lightboxItem} />
      </LayoutGroup>

      {/* System Impact Metrics */}
      <PremiumMetrics />

      {/* Final CTA - Enrollment moment */}
      <section id="landing-cta" className="py-24 md:py-32 px-4 bg-background scroll-mt-20">
        <div className="container mx-auto max-w-[var(--landing-max-width)] text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)]">
              Work faster. Work better.
            </h2>
            <p className="text-[var(--font-body-lg)] text-muted-foreground mb-8 max-w-xl mx-auto leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
              For professionals who generate leads and value their time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Conversion CTA */}
      <ConversionCTA />

      {/* Partner Section */}
      <section className="py-16 px-4 bg-background border-y border-border/30">
        <div className="container mx-auto max-w-[var(--landing-max-width)]">
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <p className="text-sm text-muted-foreground mb-6 uppercase tracking-wider">Partner</p>
            <div className="flex items-center justify-center">
              <img 
                src={emeLogo} 
                alt="EME" 
                className="h-8 opacity-60 hover:opacity-100 transition-opacity"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
